import { cloneParameters } from "./util.mjs";
import { BasePricing } from "./base.mjs";
//#region src/editGen.ts
var EditPricing = class EditPricing extends BasePricing {
	static CHAT_EDITING_DEFAULT_PRICE = 3e3;
	getChatPrice() {
		const chatParameters = cloneParameters(this.parameters["chat"]);
		const priceList = cloneParameters(this.config.chatPriceList ?? []);
		const modelId = chatParameters.modelId;
		const chatPrice = priceList.find((p) => p.modelId === modelId) ?? {
			modelId: "default",
			price: EditPricing.CHAT_EDITING_DEFAULT_PRICE,
			originalPrice: EditPricing.CHAT_EDITING_DEFAULT_PRICE
		};
		return this.applyImagePriceMatrix(chatPrice);
	}
	/**
	* Falls through to the original `chatPrice` when an axis value is missing without
	* a default, or when neither the request key nor the defaults key matches an item.
	*/
	applyImagePriceMatrix(chatPrice) {
		const matrix = chatPrice.imagePriceMatrix;
		if (!matrix || matrix.axes.length === 0) return chatPrice;
		const modelConfig = this.parameters.chat.modelConfig;
		const requestKey = {};
		for (const axis of matrix.axes) {
			const value = modelConfig?.[axis] ?? matrix.defaults?.[axis];
			if (value == null) return chatPrice;
			requestKey[axis] = value;
		}
		const findByKey = (key) => matrix.items.find((item) => matrix.axes.every((axis) => item.key[axis] === key[axis]));
		const hit = findByKey(requestKey) ?? (matrix.defaults ? findByKey(matrix.defaults) : void 0);
		if (!hit) return chatPrice;
		return {
			...chatPrice,
			price: hit.price,
			originalPrice: hit.originalPrice ?? hit.price
		};
	}
	calcChatBasePrice(chatPrice) {
		const priceToAdd = chatPrice.originalPrice ? chatPrice.originalPrice - this.config.basePrice : chatPrice.price - this.config.basePrice;
		return {
			cumulative: (i) => i + priceToAdd,
			item: {
				key: `chat-price-${chatPrice.modelId}`,
				name: `chat price ${chatPrice.modelId}`
			}
		};
	}
	calcChatDiscountPrice(chatPrice) {
		if (chatPrice.originalPrice && chatPrice.originalPrice !== chatPrice.price) {
			const diff = chatPrice.originalPrice - chatPrice.price;
			return {
				cumulative: (i) => i - diff,
				item: {
					key: `chat-price-${chatPrice.modelId}-discount`,
					name: `chat price ${chatPrice.modelId} discount`
				}
			};
		}
	}
	calcInputImageCountPrice(chatPrice) {
		if (chatPrice.inputImageCount) {
			const chatParameters = cloneParameters(this.parameters["chat"]);
			const { freeImages, step, pricePerStep } = chatPrice.inputImageCount;
			const inputImageCount = chatParameters.mediaIds?.length || 0;
			const pricingImages = Math.max(0, inputImageCount - freeImages);
			const price = Math.ceil(pricingImages / step) * pricePerStep;
			if (price > 0) return {
				cumulative: (i) => i + price,
				item: {
					key: `chat-price-${chatPrice.modelId}-input-images`,
					name: `chat price ${chatPrice.modelId} input images`
				}
			};
		}
	}
	calcOutputResolutionPrice(chatPrice) {
		if (chatPrice.outputImageResolution) {
			const currentSelectedResolution = cloneParameters(this.parameters["chat"]).modelConfig?.resolution;
			if (currentSelectedResolution) {
				const resolutionPriceItem = chatPrice.outputImageResolution.find((r) => r.resolution === currentSelectedResolution);
				if (resolutionPriceItem) {
					const price = resolutionPriceItem.price;
					return {
						cumulative: (i) => i + price,
						item: {
							key: `chat-price-${chatPrice.modelId}-output-resolution`,
							name: `chat price ${chatPrice.modelId} output resolution`
						}
					};
				}
			}
		}
	}
	calcGenerateImageBasePrice() {
		const basePrice = this.config.basePrice;
		return {
			cumulative: () => basePrice,
			item: {
				key: "generate-image",
				name: "generate image"
			}
		};
	}
	getFinalPrice() {
		const chatPrice = this.getChatPrice();
		this.meter.addResult(this.calcGenerateImageBasePrice());
		this.meter.addResult(this.calcChatBasePrice(chatPrice));
		this.meter.addResult(this.calcChatDiscountPrice(chatPrice));
		this.meter.addResult(this.calcInputImageCountPrice(chatPrice));
		this.meter.addResult(this.calcOutputResolutionPrice(chatPrice));
		return this.meter;
	}
};
//#endregion
export { EditPricing };
